import React, { useEffect, useMemo, useState } from "react";
import {
  exportPlatformAuditLogs,
  fetchPlatformAuditLogs,
  fetchPlatformOrganizations,
} from "../../api/adminData";
import { auditActionLabel, formatAuditLog, formatAuditTimestamp } from "../../utils/auditDisplay";
import { partitionAuditMetadata } from "../../utils/auditMetadataDisplay";
import { auditSeverityPillClass, getAuditSeverityDetail } from "../../utils/auditSeverity";
import { AuditChangeRows } from "../../components/admin/AuditLogVisual";
import { buildLoginDeviceStatusMap, loginDeviceStatusLabel } from "../../utils/loginDeviceAuditStatus";
import { buildSuspiciousLoginAuditMap } from "../../utils/suspiciousLoginAudit";
import { getDeviceLabelFromUserAgent } from "../../utils/userAgentLabel";

const METADATA_MAX_LEN = 240;

function formatMetadata(meta) {
  if (meta == null) return "—";
  try {
    const s = JSON.stringify(meta);
    return s.length > METADATA_MAX_LEN ? `${s.slice(0, METADATA_MAX_LEN)}…` : s;
  } catch {
    return "—";
  }
}

/** List column: readable device + IP for login; otherwise JSON summary. */
function formatMetadataListCell(row) {
  const meta = row.metadata;
  if (row.action === "login" && meta && typeof meta === "object") {
    const label = getDeviceLabelFromUserAgent(meta.user_agent);
    const ip = meta.ip_address != null && meta.ip_address !== "" ? String(meta.ip_address) : null;
    const parts = [label];
    if (ip) parts.push(ip);
    return parts.join(" · ");
  }
  return formatMetadata(meta);
}

/** Backend read-time GeoIP: city + country or fallback label. */
function formatAuditLocationLine(row) {
  if (row.action !== "login") return "";
  const city = row.location_city;
  const country = row.location_country;
  const hasCity = city != null && String(city).trim() !== "";
  const hasCountry = country != null && String(country).trim() !== "";
  if (!hasCity && !hasCountry) return "Unbekannter Ort";
  const parts = [];
  if (hasCity) parts.push(String(city).trim());
  if (hasCountry) parts.push(String(country).trim());
  return parts.join(", ");
}

function formatJsonField(value) {
  if (value == null) return "—";
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return "—";
  }
}

function loginDeviceBadgeClass(status) {
  if (status === "new") {
    return "inline-flex w-fit shrink-0 items-center rounded-full border border-amber-300 bg-amber-100 px-2 py-0.5 text-[9px] font-semibold text-amber-900 dark:border-amber-400/35 dark:bg-amber-500/15 dark:text-amber-200";
  }
  if (status === "known") {
    return "inline-flex w-fit shrink-0 items-center rounded-full border border-emerald-300 bg-emerald-100 px-2 py-0.5 text-[9px] font-semibold text-emerald-800 dark:border-emerald-400/30 dark:bg-emerald-500/15 dark:text-emerald-200";
  }
  return "inline-flex w-fit shrink-0 items-center rounded-full border border-slate-200 bg-slate-100 px-2 py-0.5 text-[9px] font-semibold text-slate-600 dark:border-white/10 dark:bg-white/[0.06] dark:text-slate-400";
}

const suspiciousBadgeClass =
  "inline-flex w-fit shrink-0 items-center rounded-full border border-rose-400/80 bg-rose-100 px-2 py-0.5 text-[9px] font-semibold text-rose-900 dark:border-rose-400/35 dark:bg-rose-500/15 dark:text-rose-200";

const filterInputClass =
  "min-w-0 rounded-[8px] border border-black/10 bg-slate-100 px-2.5 py-1.5 text-[12px] text-[#0f172a] dark:border-white/[0.08] dark:bg-[#111520] dark:text-[#eef2ff]";

/** Common audit `action` values in this codebase (exact match filter). */
const AUDIT_ACTION_OPTIONS = [
  "",
  "create",
  "update",
  "delete",
  "login",
  "impersonation_started",
];

/** Entity types used in audit writes (exact match filter). */
const ENTITY_TYPE_OPTIONS = [
  "",
  "unit",
  "tenant",
  "owner",
  "landlord",
  "property_manager",
  "inventory_item",
  "platform",
];

/**
 * Platform admin: paginated audit log rows (cross-tenant).
 */
function PlatformAuditLogsPage() {
  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(25);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedLog, setSelectedLog] = useState(null);

  const [organizations, setOrganizations] = useState([]);
  const [qInput, setQInput] = useState("");
  const [q, setQ] = useState("");
  const [action, setAction] = useState("");
  const [entityType, setEntityType] = useState("");
  const [organizationId, setOrganizationId] = useState("");
  const [actorInput, setActorInput] = useState("");
  const [actor, setActor] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [exporting, setExporting] = useState(false);

  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));

  useEffect(() => {
    let cancelled = false;
    fetchPlatformOrganizations()
      .then((data) => {
        if (!cancelled) setOrganizations(Array.isArray(data) ? data : []);
      })
      .catch(() => {
        if (!cancelled) setOrganizations([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const id = setTimeout(() => {
      const next = qInput.trim();
      setQ(next);
      setPage(1);
    }, 400);
    return () => clearTimeout(id);
  }, [qInput]);

  useEffect(() => {
    const id = setTimeout(() => {
      const next = actorInput.trim();
      setActor(next);
      setPage(1);
    }, 400);
    return () => clearTimeout(id);
  }, [actorInput]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError("");
    fetchPlatformAuditLogs({
      page,
      pageSize,
      q,
      action,
      entityType,
      organizationId,
      actor,
      dateFrom,
      dateTo,
    })
      .then((data) => {
        if (!cancelled) {
          const items = data?.items;
          setRows(Array.isArray(items) ? items : []);
          setTotalCount(typeof data?.total_count === "number" ? data.total_count : 0);
        }
      })
      .catch((e) => {
        if (!cancelled) setError(e.message || "Laden fehlgeschlagen.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [page, pageSize, q, action, entityType, organizationId, actor, dateFrom, dateTo]);

  const hasActiveFilters =
    qInput.trim() !== "" ||
    action !== "" ||
    entityType !== "" ||
    organizationId !== "" ||
    actorInput.trim() !== "" ||
    dateFrom !== "" ||
    dateTo !== "";

  const resetFilters = () => {
    setQInput("");
    setQ("");
    setAction("");
    setEntityType("");
    setOrganizationId("");
    setActorInput("");
    setActor("");
    setDateFrom("");
    setDateTo("");
    setPage(1);
  };

  const handleExportCsv = async () => {
    setExporting(true);
    setError("");
    try {
      const blob = await exportPlatformAuditLogs({
        q,
        action,
        entityType,
        organizationId,
        actor,
        dateFrom,
        dateTo,
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "audit_logs_export.csv";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(e.message || "Export fehlgeschlagen.");
    } finally {
      setExporting(false);
    }
  };

  const loginDeviceStatusById = useMemo(() => buildLoginDeviceStatusMap(rows), [rows]);
  const suspiciousLoginById = useMemo(() => buildSuspiciousLoginAuditMap(rows), [rows]);

  const selectedLogFormatted = useMemo(() => {
    if (!selectedLog || selectedLog.action === "login") return null;
    return formatAuditLog(selectedLog, { entityType: selectedLog.target_type });
  }, [selectedLog]);

  const selectedLogMetaPartition = useMemo(
    () =>
      selectedLog
        ? partitionAuditMetadata(selectedLog.metadata)
        : { known: [], rows: [], remainder: null },
    [selectedLog]
  );

  const selectedLogSeverityDetail = useMemo(
    () => (selectedLog ? getAuditSeverityDetail(selectedLog) : { level: "info", label: "Info" }),
    [selectedLog]
  );

  const detailTextClass = "mt-1 text-[13px] text-[#0f172a] dark:text-[#e2e8f0]";
  const detailJsonClass =
    "mt-1 max-h-40 overflow-auto whitespace-pre-wrap break-all rounded-[8px] border border-black/[0.08] bg-[#f8fafc] p-2 font-mono text-[11px] leading-relaxed text-[#0f172a] dark:border-white/[0.08] dark:bg-[#0c1018] dark:text-[#e2e8f0]";

  const showPageOverlay = loading && rows.length > 0;
  const showInitialLoading = loading && rows.length === 0;

  return (
    <div className="w-full max-w-none bg-[#f8fafc] px-4 py-6 text-[#0f172a] [color-scheme:light] dark:bg-[#07090f] dark:text-[#eef2ff] dark:[color-scheme:dark]">
      {selectedLog ? (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/45 p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="audit-log-detail-title"
          onClick={() => setSelectedLog(null)}
        >
          <div
            className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-[12px] border border-black/10 bg-white p-4 shadow-lg dark:border-white/[0.12] dark:bg-[#141824]"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mb-3 flex items-start justify-between gap-3">
              <h2 id="audit-log-detail-title" className="text-[16px] font-bold">
                Eintrag
              </h2>
              <button
                type="button"
                onClick={() => setSelectedLog(null)}
                className="shrink-0 rounded-[8px] border border-black/10 bg-[#f8fafc] px-3 py-1.5 text-[12px] font-semibold text-[#0f172a] hover:bg-black/[0.04] dark:border-white/[0.12] dark:bg-[#0c1018] dark:text-[#eef2ff] dark:hover:bg-white/[0.06]"
              >
                Schliessen
              </button>
            </div>
            {selectedLogFormatted ? (
              <div className="mb-4 rounded-[10px] border border-black/[0.08] bg-[#f8fafc] p-3 dark:border-white/[0.1] dark:bg-[#0c1018]">
                <div className="text-[11px] font-semibold uppercase tracking-wide text-[#64748b] dark:text-[#94a3b8]">
                  Inhalt
                </div>
                <p className={`mt-1 ${detailTextClass}`}>{selectedLogFormatted.summary}</p>
                {selectedLogFormatted.changes.length > 0 ? (
                  <AuditChangeRows changes={selectedLogFormatted.changes} listClassName="mt-3" />
                ) : null}
              </div>
            ) : null}
            <dl className="space-y-3 text-[13px]">
              <div>
                <dt className="text-[11px] font-semibold uppercase tracking-wide text-[#64748b] dark:text-[#94a3b8]">
                  Zeit
                </dt>
                <dd className={detailTextClass}>
                  {formatAuditTimestamp(selectedLog.created_at)}
                </dd>
              </div>
              <div>
                <dt className="text-[11px] font-semibold uppercase tracking-wide text-[#64748b] dark:text-[#94a3b8]">
                  Akteur
                </dt>
                <dd className={detailTextClass}>
                  {selectedLog.actor_email || selectedLog.actor_user_id || "—"}
                </dd>
              </div>
              <div>
                <dt className="text-[11px] font-semibold uppercase tracking-wide text-[#64748b] dark:text-[#94a3b8]">
                  Aktion
                </dt>
                <dd className={detailTextClass}>{auditActionLabel(selectedLog.action)}</dd>
              </div>
              <div>
                <dt className="text-[11px] font-semibold uppercase tracking-wide text-[#64748b] dark:text-[#94a3b8]">
                  Einstufung
                </dt>
                <dd className={detailTextClass}>
                  <span
                    className={`inline-flex w-fit items-center rounded-full border px-2 py-0.5 text-[10px] font-semibold ${auditSeverityPillClass(selectedLogSeverityDetail.level)}`}
                  >
                    {selectedLogSeverityDetail.label}
                  </span>
                </dd>
              </div>
              {selectedLog.action === "login" &&
              (suspiciousLoginById.get(String(selectedLog.id))?.suspicious ? (
                <div>
                  <dt className="text-[11px] font-semibold uppercase tracking-wide text-[#64748b] dark:text-[#94a3b8]">
                    Auffälligkeit
                  </dt>
                  <dd className={detailTextClass}>
                    <ul className="mt-1 list-disc space-y-1 pl-4 text-[12px] text-[#0f172a] dark:text-[#e2e8f0]">
                      {(suspiciousLoginById.get(String(selectedLog.id))?.reasons || []).map((r, idx) => (
                        <li key={idx}>{r}</li>
                      ))}
                    </ul>
                  </dd>
                </div>
              ) : null)}
              <div>
                <dt className="text-[11px] font-semibold uppercase tracking-wide text-[#64748b] dark:text-[#94a3b8]">
                  Organisation
                </dt>
                <dd className={detailTextClass}>
                  {selectedLog.organization_name || selectedLog.organization_id || "—"}
                </dd>
              </div>
              <div>
                <dt className="text-[11px] font-semibold uppercase tracking-wide text-[#64748b] dark:text-[#94a3b8]">
                  Entitätstyp
                </dt>
                <dd className={detailTextClass}>
                  {selectedLog.target_type != null && selectedLog.target_type !== ""
                    ? selectedLog.target_type
                    : "—"}
                </dd>
              </div>
              <div>
                <dt className="text-[11px] font-semibold uppercase tracking-wide text-[#64748b] dark:text-[#94a3b8]">
                  Entitäts-ID
                </dt>
                <dd className={detailTextClass}>
                  {selectedLog.target_id != null && selectedLog.target_id !== ""
                    ? selectedLog.target_id
                    : "—"}
                </dd>
              </div>
              {selectedLog.action === "login" &&
              selectedLog.metadata &&
              typeof selectedLog.metadata === "object" ? (
                <>
                  <div>
                    <dt className="text-[11px] font-semibold uppercase tracking-wide text-[#64748b] dark:text-[#94a3b8]">
                      Gerät
                    </dt>
                    <dd className={detailTextClass}>
                      {getDeviceLabelFromUserAgent(selectedLog.metadata.user_agent)}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-[11px] font-semibold uppercase tracking-wide text-[#64748b] dark:text-[#94a3b8]">
                      Status
                    </dt>
                    <dd className={detailTextClass}>
                      {loginDeviceStatusLabel(loginDeviceStatusById.get(selectedLog.id))}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-[11px] font-semibold uppercase tracking-wide text-[#64748b] dark:text-[#94a3b8]">
                      IP-Adresse
                    </dt>
                    <dd className={detailTextClass}>
                      {selectedLog.metadata.ip_address != null &&
                      String(selectedLog.metadata.ip_address) !== ""
                        ? String(selectedLog.metadata.ip_address)
                        : "—"}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-[11px] font-semibold uppercase tracking-wide text-[#64748b] dark:text-[#94a3b8]">
                      Standort (ungefähr)
                    </dt>
                    <dd className={detailTextClass}>{formatAuditLocationLine(selectedLog)}</dd>
                  </div>
                  <div>
                    <dt className="text-[11px] font-semibold uppercase tracking-wide text-[#64748b] dark:text-[#94a3b8]">
                      User-Agent roh
                    </dt>
                    <dd className={detailJsonClass}>
                      {selectedLog.metadata.user_agent != null &&
                      String(selectedLog.metadata.user_agent) !== ""
                        ? String(selectedLog.metadata.user_agent)
                        : "—"}
                    </dd>
                  </div>
                </>
              ) : null}
              <details className="rounded-[8px] border border-black/10 bg-[#f8fafc] p-2 dark:border-white/[0.08] dark:bg-[#0c1018]">
                <summary className="cursor-pointer text-[12px] font-semibold text-[#0f172a] dark:text-[#e2e8f0]">
                  Rohdaten / technische Details
                </summary>
                <div className="mt-2 space-y-2">
                  <div>
                    <dt className="text-[10px] font-semibold uppercase tracking-wide text-[#64748b] dark:text-[#94a3b8]">
                      Metadaten
                    </dt>
                    <dd className={`${detailTextClass} space-y-2`}>
                      {selectedLogMetaPartition.known.length > 0 ? (
                        <dl className="space-y-2 rounded-[8px] border border-black/[0.06] bg-[#f8fafc] p-2 dark:border-white/[0.06] dark:bg-[#0c1018]">
                          {selectedLogMetaPartition.known.map((r) => (
                            <div key={r.key} className="grid gap-1 sm:grid-cols-[minmax(0,7rem)_1fr] sm:gap-2">
                              <dt className="text-[10px] font-medium text-[#64748b] dark:text-[#94a3b8]">{r.label}</dt>
                              <dd className="break-all text-[12px] text-[#0f172a] dark:text-[#e2e8f0]">{r.value}</dd>
                            </div>
                          ))}
                        </dl>
                      ) : null}
                      {selectedLogMetaPartition.remainder ? (
                        <div>
                          <div className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-[#64748b] dark:text-[#94a3b8]">
                            Weitere Felder
                          </div>
                          <div className={detailJsonClass}>{formatJsonField(selectedLogMetaPartition.remainder)}</div>
                        </div>
                      ) : null}
                      {selectedLog.metadata == null ? (
                        <span className="text-[12px]">—</span>
                      ) : selectedLogMetaPartition.known.length === 0 && !selectedLogMetaPartition.remainder ? (
                        <div className={detailJsonClass}>{formatJsonField(selectedLog.metadata)}</div>
                      ) : null}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-[10px] font-semibold uppercase tracking-wide text-[#64748b] dark:text-[#94a3b8]">
                      old_values
                    </dt>
                    <dd className={detailJsonClass}>{formatJsonField(selectedLog.old_values)}</dd>
                  </div>
                  <div>
                    <dt className="text-[10px] font-semibold uppercase tracking-wide text-[#64748b] dark:text-[#94a3b8]">
                      new_values
                    </dt>
                    <dd className={detailJsonClass}>{formatJsonField(selectedLog.new_values)}</dd>
                  </div>
                </div>
              </details>
            </dl>
          </div>
        </div>
      ) : null}

      <div className="mb-6 flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="mb-2 text-[12px] font-bold uppercase tracking-wide text-[#fb923c]">
            Vantio Platform
          </div>
          <h1 className="text-[22px] font-bold tracking-[-0.02em]">Audit-Protokoll</h1>
          <p className="mt-2 text-[13px] text-[#64748b] dark:text-[#6b7a9a]">
            Audit-Protokoll (plattformweit)
          </p>
        </div>
        <div className="flex min-w-[200px] flex-col items-end gap-1">
          <button
            type="button"
            onClick={handleExportCsv}
            disabled={exporting}
            className="rounded-[8px] border border-black/10 bg-white px-3 py-2 text-[12px] font-semibold text-[#0f172a] disabled:cursor-not-allowed disabled:opacity-50 dark:border-white/[0.12] dark:bg-[#141824] dark:text-[#eef2ff]"
          >
            {exporting ? "Export läuft…" : "Export CSV"}
          </button>
          <p className="max-w-[280px] text-right text-[11px] text-[#64748b] dark:text-[#94a3b8]">
            Exportiert die aktuell gefilterten Audit-Einträge
          </p>
        </div>
      </div>

      {error && (
        <div className="mb-4 rounded-[10px] border border-red-500/20 bg-red-500/10 px-4 py-3 text-[14px] text-[#f87171]">
          {error}
        </div>
      )}

      <div className="mb-4 rounded-[12px] border border-black/10 bg-white p-3 dark:border-white/[0.07] dark:bg-[#141824]">
        <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
          <span className="text-[11px] font-semibold uppercase tracking-wide text-[#64748b] dark:text-[#94a3b8]">
            Filter
          </span>
          <button
            type="button"
            onClick={resetFilters}
            className="rounded-[8px] border border-black/10 bg-[#f8fafc] px-2.5 py-1 text-[11px] font-semibold text-[#0f172a] hover:bg-black/[0.04] dark:border-white/[0.12] dark:bg-[#0c1018] dark:text-[#eef2ff] dark:hover:bg-white/[0.06]"
          >
            Filter zurücksetzen
          </button>
        </div>
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          <label className="flex min-w-0 flex-col gap-1">
            <span className="text-[10px] font-medium text-[#64748b] dark:text-[#94a3b8]">Suche</span>
            <input
              type="search"
              value={qInput}
              onChange={(e) => setQInput(e.target.value)}
              placeholder="Suche nach Akteur, Aktion, Entität oder ID"
              className={filterInputClass}
              autoComplete="off"
            />
          </label>
          <label className="flex min-w-0 flex-col gap-1">
            <span className="text-[10px] font-medium text-[#64748b] dark:text-[#94a3b8]">Aktion</span>
            <select
              value={action}
              onChange={(e) => {
                setAction(e.target.value);
                setPage(1);
              }}
              className={filterInputClass}
            >
              <option value="">Alle Aktionen</option>
              {AUDIT_ACTION_OPTIONS.filter(Boolean).map((val) => (
                <option key={val} value={val}>
                  {val}
                </option>
              ))}
            </select>
          </label>
          <label className="flex min-w-0 flex-col gap-1">
            <span className="text-[10px] font-medium text-[#64748b] dark:text-[#94a3b8]">Entitätstyp</span>
            <select
              value={entityType}
              onChange={(e) => {
                setEntityType(e.target.value);
                setPage(1);
              }}
              className={filterInputClass}
            >
              <option value="">Alle Typen</option>
              {ENTITY_TYPE_OPTIONS.filter(Boolean).map((val) => (
                <option key={val} value={val}>
                  {val}
                </option>
              ))}
            </select>
          </label>
          <label className="flex min-w-0 flex-col gap-1">
            <span className="text-[10px] font-medium text-[#64748b] dark:text-[#94a3b8]">Organisation</span>
            <select
              value={organizationId}
              onChange={(e) => {
                setOrganizationId(e.target.value);
                setPage(1);
              }}
              className={filterInputClass}
            >
              <option value="">Alle Organisationen</option>
              {organizations.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.name || o.id}
                </option>
              ))}
            </select>
          </label>
          <label className="flex min-w-0 flex-col gap-1">
            <span className="text-[10px] font-medium text-[#64748b] dark:text-[#94a3b8]">Akteur</span>
            <input
              type="text"
              value={actorInput}
              onChange={(e) => setActorInput(e.target.value)}
              placeholder="E-Mail oder User-ID"
              className={filterInputClass}
              autoComplete="off"
            />
          </label>
          <label className="flex min-w-0 flex-col gap-1">
            <span className="text-[10px] font-medium text-[#64748b] dark:text-[#94a3b8]">Von Datum</span>
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => {
                setDateFrom(e.target.value);
                setPage(1);
              }}
              className={filterInputClass}
            />
          </label>
          <label className="flex min-w-0 flex-col gap-1">
            <span className="text-[10px] font-medium text-[#64748b] dark:text-[#94a3b8]">Bis Datum</span>
            <input
              type="date"
              value={dateTo}
              onChange={(e) => {
                setDateTo(e.target.value);
                setPage(1);
              }}
              className={filterInputClass}
            />
          </label>
        </div>
        {hasActiveFilters ? (
          <p className="mt-2 text-[11px] text-[#64748b] dark:text-[#94a3b8]">Filter aktiv</p>
        ) : null}
      </div>

      {showInitialLoading ? (
        <div className="flex min-h-[120px] items-center justify-center rounded-[12px] border border-black/10 bg-white dark:border-white/[0.07] dark:bg-[#141824]">
          <p className="text-[13px] text-[#64748b] dark:text-[#6b7a9a]">Lade …</p>
        </div>
      ) : (
        <div className="relative overflow-x-auto rounded-[12px] border border-black/10 bg-white dark:border-white/[0.07] dark:bg-[#141824]">
          {showPageOverlay ? (
            <div
              className="absolute inset-0 z-10 flex items-center justify-center bg-white/70 dark:bg-[#141824]/75"
              aria-busy="true"
              aria-live="polite"
            >
              <p className="text-[13px] text-[#64748b] dark:text-[#6b7a9a]">Lade …</p>
            </div>
          ) : null}
          <table className="w-full min-w-[720px] border-collapse text-left text-[12px]">
            <thead>
              <tr className="border-b border-black/10 dark:border-white/[0.08]">
                <th className="px-3 py-2.5 font-semibold text-[#64748b] dark:text-[#94a3b8]">
                  Zeit
                </th>
                <th className="px-3 py-2.5 font-semibold text-[#64748b] dark:text-[#94a3b8]">
                  Akteur
                </th>
                <th className="px-3 py-2.5 font-semibold text-[#64748b] dark:text-[#94a3b8]">
                  Aktion
                </th>
                <th className="px-3 py-2.5 font-semibold text-[#64748b] dark:text-[#94a3b8]">
                  Organisation
                </th>
                <th className="px-3 py-2.5 font-semibold text-[#64748b] dark:text-[#94a3b8]">
                  Metadaten
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-3 py-6 text-center text-[#64748b] dark:text-[#6b7a9a]">
                    Keine Einträge gefunden
                  </td>
                </tr>
              ) : (
                rows.map((row) => {
                  const rowSev = getAuditSeverityDetail(row);
                  return (
                  <tr
                    key={row.id}
                    role="button"
                    tabIndex={0}
                    onClick={() => setSelectedLog(row)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        setSelectedLog(row);
                      }
                    }}
                    className="cursor-pointer border-b border-black/[0.06] hover:bg-black/[0.03] dark:border-white/[0.05] dark:hover:bg-white/[0.04]"
                  >
                    <td className="whitespace-nowrap px-3 py-2 align-top text-[11px] text-[#64748b] dark:text-[#94a3b8]">
                      {formatAuditTimestamp(row.created_at)}
                    </td>
                    <td className="max-w-[180px] px-3 py-2 align-top break-all">
                      {row.actor_email || row.actor_user_id || "—"}
                    </td>
                    <td className="max-w-[200px] px-3 py-2 align-top font-medium">
                      <div className="flex flex-wrap items-center gap-1.5">
                        <span
                          className={`inline-flex shrink-0 items-center rounded-full border px-1.5 py-0.5 text-[9px] font-semibold leading-none ${auditSeverityPillClass(rowSev.level)}`}
                          title={rowSev.label}
                        >
                          {rowSev.label}
                        </span>
                        <span className="min-w-0">{auditActionLabel(row.action)}</span>
                      </div>
                    </td>
                    <td className="max-w-[200px] px-3 py-2 align-top break-all">
                      {row.organization_name || row.organization_id || "—"}
                    </td>
                    <td className="max-w-[280px] px-3 py-2 align-top text-[11px] text-[#64748b] dark:text-[#94a3b8]">
                      <div className="flex flex-col gap-1">
                        {row.action === "login" ? (
                          <div className="flex flex-wrap items-center gap-1">
                            <span className={loginDeviceBadgeClass(loginDeviceStatusById.get(row.id))}>
                              {loginDeviceStatusLabel(loginDeviceStatusById.get(row.id))}
                            </span>
                            {suspiciousLoginById.get(String(row.id))?.suspicious ? (
                              <span className={suspiciousBadgeClass}>Auffällig</span>
                            ) : null}
                          </div>
                        ) : null}
                        <span className="break-all">{formatMetadataListCell(row)}</span>
                        {row.action === "login" ? (
                          <span className="text-[10px] leading-snug text-[#64748b] dark:text-[#94a3b8]">
                            {formatAuditLocationLine(row)}
                          </span>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      )}

      {!showInitialLoading ? (
        <div className="mt-4 flex flex-wrap items-center gap-2 text-[12px] text-[#0f172a] dark:text-[#e2e8f0]">
          <button
            type="button"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={loading || page <= 1}
            className="rounded-[8px] border border-black/10 bg-white px-3 py-1.5 font-medium disabled:cursor-not-allowed disabled:opacity-50 dark:border-white/[0.12] dark:bg-[#141824]"
          >
            Zurück
          </button>
          {totalPages <= 15
            ? Array.from({ length: totalPages }, (_, i) => i + 1).map((n) => (
                <button
                  key={n}
                  type="button"
                  onClick={() => setPage(n)}
                  disabled={loading || n === page}
                  className={`min-w-[2rem] rounded-[8px] border px-2 py-1.5 font-medium disabled:cursor-default ${
                    n === page
                      ? "border-[#fb923c] bg-[#fff7ed] text-[#c2410c] dark:border-[#fb923c]/50 dark:bg-[#431407]/40 dark:text-[#fed7aa]"
                      : "border-black/10 bg-white dark:border-white/[0.12] dark:bg-[#141824]"
                  } ${loading ? "opacity-50" : ""}`}
                >
                  {n}
                </button>
              ))
            : null}
          <button
            type="button"
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={loading || page >= totalPages}
            className="rounded-[8px] border border-black/10 bg-white px-3 py-1.5 font-medium disabled:cursor-not-allowed disabled:opacity-50 dark:border-white/[0.12] dark:bg-[#141824]"
          >
            Weiter
          </button>
          <span className="ml-1 text-[#64748b] dark:text-[#94a3b8]">
            Seite {page} von {totalPages}
          </span>
        </div>
      ) : null}
    </div>
  );
}

export default PlatformAuditLogsPage;
